import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Checkbox } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Skeleton } from '@material-ui/lab';
import cx from 'classnames';
import { TrackerObjectiveValueTypes } from 'constants/assignments';
import { isNil } from 'lodash';
import { useInsightsClassroomTrackerInstanceSectionDetail } from 'sdk';

import { ReactComponent as PartialUploadIcon } from 'pages/Teachers/ClassroomInsights/assets/partial-upload-icon.svg';
import { ReactComponent as PublishedIcon } from 'pages/Teachers/ClassroomInsights/assets/published-icon.svg';
import { ReactComponent as NeedsReviewIcon } from 'pages/Teachers/ClassroomInsights/assets/reupload-icon.svg';
import { ReactComponent as UncheckIcon } from 'pages/Teachers/ClassroomInsights/assets/uncheck-icon.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import {
  MASTERY_LEVELS,
  STATUS_ICON_MAPPER,
  STATUS_TEXT_MAPPER,
  UPPERCASE_GRADE_TYPES
} from 'pages/Teachers/ClassroomInsights/constants';
import {
  getFormattedGrade,
  getStatusFlags,
  renderCheckIcon
} from 'utils/teachers/classroomInsights';

import styles from './style.module.scss';

const getCardClassName = ({
  isCelebrate,
  isSupport,
  isIntervene,
  isSelected
}) => {
  return cx(styles.studentProgressBox, {
    [styles.cardCelebrate]: isCelebrate,
    [styles.cardSupport]: isSupport,
    [styles.cardIntervene]: isIntervene,
    [styles.cardNeutral]: !isCelebrate && !isSupport && !isIntervene,
    [styles.selectedBorder]: isSelected
  });
};

const renderGradeValue = ({ formattedGrade, valueType }) => {
  if (isNil(formattedGrade)) return '-';

  if (valueType === TrackerObjectiveValueTypes.CHECK_GRADE) {
    return (
      <CheckGradeIcon grade={formattedGrade} objectiveValueType={valueType} />
    );
  }

  return UPPERCASE_GRADE_TYPES.includes(valueType)
    ? formattedGrade?.toUpperCase()
    : formattedGrade;
};

const StudentProgressBox = ({
  name,
  status,
  masteryLabel,
  grade,
  tooltipStyles,
  isSelected,
  isPublished,
  isPartialUpload,
  onClick,
  onSelect
}) => {
  const [isPublishedHovered, setIsPublishedHovered] = useState(false);

  const { trackerInstanceSectionId } = useParams();
  const { data: sectionDetails } =
    useInsightsClassroomTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  const isLoading = status === MASTERY_LEVELS.LOADING;

  const flags = getStatusFlags({ status, masteryLabel, isPartialUpload });

  const {
    isPendingUpload,
    isNeedsReview,
    isCelebrate,
    isSupport,
    isIntervene,
    showStatusPill,
    showCheckIcon
  } = flags;

  const valueType = sectionDetails?.value_type;
  const formattedGrade = getFormattedGrade({ grade, valueType });

  const handleOnClick = () => {
    if (masteryLabel !== MASTERY_LEVELS.NEED_UPLOAD && !isPendingUpload) {
      onClick();
    }
  };

  return isLoading ? (
    <div className={styles.studentProgressBox}>
      <div className={styles.student}>
        <p className={styles.studentName}>{name}</p>
        <div>
          <CircularProgress size={15} color="secondary" />
        </div>
      </div>
      <div className={styles.studentInfo}>
        <Skeleton variant="text" width={100} height={24} />
        <Skeleton variant="text" width={30} height={24} />
      </div>
    </div>
  ) : (
    <div
      className={cx(styles.studentProgressContainer, {
        [styles.hoverable]: !isPendingUpload
      })}
      onClick={handleOnClick}
    >
      <div
        className={getCardClassName({
          isCelebrate,
          isSupport,
          isIntervene,
          isSelected
        })}
      >
        <div className={styles.studentRow}>
          <div className={styles.student}>
            {!isPendingUpload && (
              <Tooltip
                title="Select for bulk actions"
                classes={{ tooltip: tooltipStyles?.customTooltip }}
                placement="top"
              >
                <div
                  className={styles.checkboxWrapper}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    className={styles.checkbox}
                    checked={isSelected}
                    onChange={onSelect}
                    disableRipple
                    icon={<span className={styles.uncheckedIcon} />}
                    checkedIcon={
                      <div className={styles.checkedIcon}>
                        <UncheckIcon />
                      </div>
                    }
                  />
                </div>
              </Tooltip>
            )}
            <Tooltip
              title={name}
              classes={{ tooltip: tooltipStyles?.customTooltip }}
              placement="top"
            >
              <p className={styles.studentName}>{name}</p>
            </Tooltip>
          </div>

          <Tooltip
            title={
              isPartialUpload
                ? 'Partially Uploaded'
                : STATUS_TEXT_MAPPER[status] || ''
            }
            classes={{ tooltip: tooltipStyles?.customTooltip }}
            placement="top"
          >
            <div className={styles.statusIconWrapper}>
              {isPartialUpload ? (
                <PartialUploadIcon className={styles.partialStatusIcon} />
              ) : (
                STATUS_ICON_MAPPER[status]
              )}
            </div>
          </Tooltip>
        </div>

        <div className={styles.studentInfo}>
          <div className={styles.leftInfoGroup}>
            {showCheckIcon &&
              renderCheckIcon({ isCelebrate, isSupport, isIntervene })}{' '}
            {showStatusPill && (
              <Tooltip
                title={
                  isPublishedHovered
                    ? ''
                    : isPartialUpload
                    ? 'Partially Upload'
                    : status
                }
                classes={{ tooltip: tooltipStyles?.customTooltip }}
              >
                <span
                  className={cx(styles.statusPill, {
                    [styles.statusPillPartialUpload]: isPartialUpload,
                    [styles.statusPillPendingUpload]: isPendingUpload,
                    [styles.statusPillNeedsReview]: isNeedsReview
                  })}
                >
                  {isNeedsReview && (
                    <NeedsReviewIcon className={styles.pillIcon} />
                  )}
                  <span className={styles.statusText}>
                    {isPartialUpload
                      ? 'Partially Upload'
                      : isNeedsReview
                      ? 'Reupload Needed'
                      : 'Pending Upload'}
                  </span>

                  {isPublished && !isNeedsReview && !isPartialUpload && (
                    <Tooltip
                      title="Successfully synced and published"
                      classes={{ tooltip: tooltipStyles?.customTooltip }}
                    >
                      <span
                        onMouseEnter={() => setIsPublishedHovered(true)}
                        onMouseLeave={() => setIsPublishedHovered(false)}
                        className={styles.publishedBadge}
                      >
                        <PublishedIcon />
                      </span>
                    </Tooltip>
                  )}
                </span>
              </Tooltip>
            )}
          </div>

          <span className={styles.grade}>
            {renderGradeValue({ formattedGrade, valueType })}{' '}
          </span>
        </div>
      </div>

      <VisibilityIcon className={styles.viewIcon} color="primary" />
    </div>
  );
};

export default StudentProgressBox;
